import { Box, ButtonBase, Typography } from "@mui/material";
// Thumbnails
import bentoBasicsThumb from "assets/home/bento_basics.png";
import buildingRelationshipsThumb from "assets/home/building_relationships.png";
import buildingSystemsThumb from "assets/home/building_systems.png";
import consistentIncomeThumb from "assets/home/consistent_income.png";
import followingUpThumb from "assets/home/following_up.png";
import getInboundThumb from "assets/home/get_inbound.png";
import gettingStartedThumb from "assets/home/getting_started.png";
import improveResultsThumb from "assets/home/improve_results.png";
import influencerGrowthThumb from "assets/home/influencer_growth.png";
import landingPrThumb from "assets/home/landing_pr.png";
import personalizeOutreachThumb from "assets/home/personalize_outreach.png";
import pitchEmailThumb from "assets/home/pitch_email.png";
import portfolioTipsThumb from "assets/home/portfolio_tips.png";
import positionYourselfThumb from "assets/home/position_yourself.png";
import pricingContentThumb from "assets/home/pricing_content.png";
import retainerClientThumb from "assets/home/retainer_client.png";
import scriptWritingThumb from "assets/home/script_writing.png";
import scriptingTipsThumb from "assets/home/scripting_tips.png";
import ugcExamplesThumb from "assets/home/ugc_examples.png";
import whatIsUgcThumb from "assets/home/what_is_ugc.png";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext } from "react";
import { BrandDealJourney } from "schemas/dashboard";

import { isInfluencer, isUgcCreator } from "utils/influencer";
import { trackEvent } from "utils/tracking";

type VideoTip = {
  title: string;
  url: string;
  thumbnail: string;
};

// UGC creators - brand new or done some work
const UGC_BEGINNER_VIDEOS: VideoTip[] = [
  {
    title: "What is UGC",
    url: "https://www.instagram.com/reel/DD-PoyFSSQl/",
    thumbnail: whatIsUgcThumb,
  },
  {
    title: "How to get started as a UGC creator",
    url: "https://www.instagram.com/reel/DSU-_a7DlJ7/",
    thumbnail: gettingStartedThumb,
  },
  {
    title: "Bento Basics",
    url: "https://www.instagram.com/p/DTqa98eCOj8/",
    thumbnail: bentoBasicsThumb,
  },
  {
    title: "How to position yourself to a brand",
    url: "https://www.instagram.com/p/DRdpKOYEf_j/",
    thumbnail: positionYourselfThumb,
  },
  {
    title: "Portfolio tips",
    url: "https://www.instagram.com/reel/C-NsSVbPO2v/",
    thumbnail: portfolioTipsThumb,
  },
  {
    title: "UGC example",
    url: "https://www.instagram.com/reel/DS3gd2VAV9m/",
    thumbnail: ugcExamplesThumb,
  },
  {
    title: "Script writing for UGC",
    url: "https://www.instagram.com/reel/DUO9APIEXQV/",
    thumbnail: scriptWritingThumb,
  },
];

// Influencer - brand new or done some work
const INFLUENCER_BEGINNER_VIDEOS: VideoTip[] = [
  {
    title: "Personalizing your outreach",
    url: "https://www.instagram.com/p/DFVjVxIR7xP/",
    thumbnail: personalizeOutreachThumb,
  },
  {
    title: "Writing a strong pitch email",
    url: "https://www.instagram.com/p/DNWuCl5M34F/",
    thumbnail: pitchEmailThumb,
  },
  {
    title: "Bento Basics",
    url: "https://www.instagram.com/p/DTqa98eCOj8/",
    thumbnail: bentoBasicsThumb,
  },
  {
    title: "Getting inbound UGC deals",
    url: "https://www.instagram.com/p/DTd0P2Sj6ZI/",
    thumbnail: getInboundThumb,
  },
  {
    title: "Following up with past brands",
    url: "https://www.instagram.com/p/DQHQPwFjVy5/",
    thumbnail: followingUpThumb,
  },
];

// UGC or Influencer - tried pitching, want better results
const TRIED_PITCHING_VIDEOS: VideoTip[] = [
  {
    title: "Writing an influencer pitch email",
    url: "https://www.instagram.com/p/DNWuCl5M34F/",
    thumbnail: pitchEmailThumb,
  },
  {
    title: "How to improve your results",
    url: "https://www.instagram.com/p/DUBbjpSCOal/",
    thumbnail: improveResultsThumb,
  },
  {
    title: "Personalizing your outreach",
    url: "https://www.instagram.com/p/DFVjVxIR7xP/",
    thumbnail: personalizeOutreachThumb,
  },
  {
    title: "How to position yourself to a brand",
    url: "https://www.instagram.com/p/DRdpKOYEf_j/",
    thumbnail: positionYourselfThumb,
  },
  {
    title: "Landing PR",
    url: "https://www.instagram.com/p/DLXYWDcgzRE/",
    thumbnail: landingPrThumb,
  },
  {
    title: "Getting inbound UGC deals",
    url: "https://www.instagram.com/p/DTd0P2Sj6ZI/",
    thumbnail: getInboundThumb,
  },
  {
    title: "Following up with past brands",
    url: "https://www.instagram.com/p/DQHQPwFjVy5/",
    thumbnail: followingUpThumb,
  },
];

// Influencer - work regularly
const INFLUENCER_ADVANCED_VIDEOS: VideoTip[] = [
  {
    title: "Writing an influencer pitch email",
    url: "https://www.instagram.com/p/DNWuCl5M34F/",
    thumbnail: pitchEmailThumb,
  },
  {
    title: "Growing as an influencer",
    url: "https://www.instagram.com/p/DTB5qBngRzt/",
    thumbnail: influencerGrowthThumb,
  },
  {
    title: "What is a retainer client",
    url: "https://www.instagram.com/p/C_a1t3Hvr8N/",
    thumbnail: retainerClientThumb,
  },
  {
    title: "Personalizing your outreach",
    url: "https://www.instagram.com/p/DFVjVxIR7xP/",
    thumbnail: personalizeOutreachThumb,
  },
  {
    title: "How to position yourself to a brand",
    url: "https://www.instagram.com/p/DRdpKOYEf_j/",
    thumbnail: positionYourselfThumb,
  },
  {
    title: "Pricing your content",
    url: "https://www.instagram.com/p/DTtXo1kAVEE/",
    thumbnail: pricingContentThumb,
  },
  {
    title: "Scripting tips",
    url: "https://www.instagram.com/p/DUAiDL_k8eZ/",
    thumbnail: scriptingTipsThumb,
  },
  {
    title: "Building relationships & retainers",
    url: "https://www.instagram.com/p/DTbVHrXEU5t/",
    thumbnail: buildingRelationshipsThumb,
  },
  {
    title: "Creating consistent income",
    url: "https://www.instagram.com/p/DQ49bdADR9g/",
    thumbnail: consistentIncomeThumb,
  },
  {
    title: "Following up with past brands",
    url: "https://www.instagram.com/p/DQHQPwFjVy5/",
    thumbnail: followingUpThumb,
  },
];

// UGC - work regularly
const UGC_ADVANCED_VIDEOS: VideoTip[] = [
  {
    title: "How to position yourself to a brand",
    url: "https://www.instagram.com/p/DRdpKOYEf_j/",
    thumbnail: positionYourselfThumb,
  },
  {
    title: "Building systems for your UGC business",
    url: "https://www.instagram.com/p/DT0t9ookmgR/",
    thumbnail: buildingSystemsThumb,
  },
  {
    title: "Building relationships & retainers",
    url: "https://www.instagram.com/p/DTbVHrXEU5t/",
    thumbnail: buildingRelationshipsThumb,
  },
  {
    title: "What is a retainer client",
    url: "https://www.instagram.com/p/C_a1t3Hvr8N/",
    thumbnail: retainerClientThumb,
  },
  {
    title: "Following up with past brands",
    url: "https://www.instagram.com/p/DQHQPwFjVy5/",
    thumbnail: followingUpThumb,
  },
];

const getVideosForUser = (
  isUgcCreator?: boolean,
  isInfluencer?: boolean,
  brandDealJourney?: BrandDealJourney,
): VideoTip[] => {
  const isBeginner =
    !brandDealJourney ||
    brandDealJourney === BrandDealJourney.brand_new ||
    brandDealJourney === BrandDealJourney.done_some_work;
  const isTriedPitching = brandDealJourney === BrandDealJourney.tried_pitching;
  const isAdvanced = brandDealJourney === BrandDealJourney.work_regularly;

  if (isTriedPitching) {
    return TRIED_PITCHING_VIDEOS;
  }

  if (isAdvanced) {
    if (isUgcCreator && !isInfluencer) {
      return UGC_ADVANCED_VIDEOS;
    }
    return INFLUENCER_ADVANCED_VIDEOS;
  }

  if (isBeginner) {
    if (isUgcCreator || !isInfluencer) {
      return UGC_BEGINNER_VIDEOS;
    }
    return INFLUENCER_BEGINNER_VIDEOS;
  }

  return UGC_BEGINNER_VIDEOS;
};

const styles = {
  container: {
    mx: 1,
  },
  title: {
    fontWeight: 600,
    fontSize: { xs: 18, md: 22 },
  },
  scrollContainer: {
    display: "flex",
    gap: 2,
    overflowX: "auto",
    py: 2,
    alignItems: "flex-start",
  },
  videoItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: 100,
    maxWidth: 100,
  },
  bubble: {
    width: 80,
    height: 80,
    borderRadius: "50%",
    background:
      "linear-gradient(135deg, #833AB4 0%, #FD1D1D 50%, #FCB045 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    "&:hover": {
      transform: "scale(1.05)",
      transition: "transform 0.2s ease-in-out",
    },
  },
  bubbleInner: {
    width: 74,
    height: 74,
    borderRadius: "50%",
    backgroundColor: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  bubbleContent: {
    width: 68,
    height: 68,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    overflow: "hidden",
    position: "relative",
  },
  thumbnailImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  playOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    borderRadius: "50%",
  },
  playIcon: {
    fontSize: 24,
    color: "white",
  },
  videoTitle: {
    mt: 1,
    fontSize: 11,
    textAlign: "center",
    color: "text.secondary",
    lineHeight: 1.2,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  instagramIcon: {
    position: "absolute",
    bottom: -2,
    right: -2,
    width: 24,
    height: 24,
    borderRadius: "50%",
    backgroundColor: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
};

const TipsFromCreators = () => {
  const { profile } = useContext(OrganizationUserContext);

  const videos = getVideosForUser(
    isUgcCreator(profile),
    isInfluencer(profile),
    profile?.brandDealJourney,
  );

  const handleVideoClick = (video: VideoTip) => {
    trackEvent("Tips From Creators Video Clicked", {
      title: video.title,
      url: video.url,
      isUgcCreator: isUgcCreator(profile),
      isInfluencer: isInfluencer(profile),
      brandDealJourney: profile?.brandDealJourney,
    });
    window.open(video.url, "_blank", "noopener,noreferrer");
  };

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>Tips From Creators</Typography>

      <Box sx={styles.scrollContainer}>
        {videos.map((video, index) => (
          <ButtonBase
            key={`tip-video-${index}`}
            onClick={() => handleVideoClick(video)}
            sx={styles.videoItem}
          >
            <Box sx={styles.bubble}>
              <Box sx={styles.bubbleInner}>
                <Box sx={styles.bubbleContent}>
                  {video.thumbnail ? (
                    <>
                      <Box
                        component="img"
                        src={video.thumbnail}
                        alt={video.title}
                        sx={styles.thumbnailImage}
                      />
                      <Box sx={styles.playOverlay}>
                        <Box
                          component="i"
                          className="fa-solid fa-play"
                          sx={styles.playIcon}
                        />
                      </Box>
                    </>
                  ) : (
                    <Box
                      component="i"
                      className="fa-solid fa-play"
                      sx={styles.playIcon}
                    />
                  )}
                </Box>
              </Box>
              <Box sx={styles.instagramIcon}>
                <Box
                  component="i"
                  className="fa-brands fa-instagram"
                  sx={{ fontSize: 14, color: "#E4405F" }}
                />
              </Box>
            </Box>
            <Typography sx={styles.videoTitle}>{video.title}</Typography>
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
};

export default TipsFromCreators;
